/**
 * Admin tool for spinning up a test company end-to-end:
 *   1. Create Salesforce Account + Contacts
 *   2. Trigger HubPlus's existing roster sync to pull the new company
 *      down into local DB (Company + Employee rows)
 *
 * Picklist dropdowns are sourced from a live SF describe via the
 * /admin/test-companies/schema endpoint — no hardcoded option lists,
 * so when SF admins change a picklist the form picks it up.
 *
 * The BE refuses to run unless connected to a sandbox SF org.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Plus, Sparkles, Trash2 } from "lucide-react";

import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { getTestCompanySchema } from "../../api/getTestCompanySchema";
import type {
  PicklistEntry,
  TestCompanySchema,
} from "../../api/getTestCompanySchema";
import { createTestCompany } from "../../api/createTestCompany";
import type {
  CreateTestCompanyEmployeeInput,
  CreateTestCompanyMembershipInput,
  CreateTestCompanyResponseData,
} from "../../api/createTestCompany";
import { generateMockTestCompany } from "../../api/generateMockTestCompany";
import { activePicklistOptions } from "../../utils/activePicklistOptions";

type AccountFieldsState = {
  Account_Status__c: string;
  Primary_Member_Email__c: string;
  Intacct_Contact_Email__c: string;
  Website: string;
  Phone: string;
  BillingStreet: string;
  BillingCity: string;
  BillingState: string;
  BillingPostalCode: string;
  BillingCountry: string;
  ShippingStreet: string;
  ShippingCity: string;
  ShippingState: string;
  ShippingPostalCode: string;
  ShippingCountry: string;
  ASI_Status__c: string;
  ESIStatus__c: string;
  PSIStatus__c: string;
  RSIStatus__c: string;
  ASI_Join_Date__c: string;
  ESI_Join_Date__c: string;
  PSI_Join_Date__c: string;
  RSI_Join_Date__c: string;
  Eagles__c: boolean;
  Stochastic_Marketing_Status__c: string;
  CertainPath_Member_Status__c: string;
  Primary_Join_Date__c: string;
};

const initialAccountFields = (): AccountFieldsState => ({
  Account_Status__c: "",
  Primary_Member_Email__c: "",
  Intacct_Contact_Email__c: "",
  Website: "",
  Phone: "",
  BillingStreet: "",
  BillingCity: "",
  BillingState: "",
  BillingPostalCode: "",
  BillingCountry: "",
  ShippingStreet: "",
  ShippingCity: "",
  ShippingState: "",
  ShippingPostalCode: "",
  ShippingCountry: "",
  ASI_Status__c: "",
  ESIStatus__c: "",
  PSIStatus__c: "",
  RSIStatus__c: "",
  ASI_Join_Date__c: "",
  ESI_Join_Date__c: "",
  PSI_Join_Date__c: "",
  RSI_Join_Date__c: "",
  Eagles__c: false,
  Stochastic_Marketing_Status__c: "",
  CertainPath_Member_Status__c: "",
  Primary_Join_Date__c: "",
});

type EmployeeRow = CreateTestCompanyEmployeeInput;

type MembershipRow = CreateTestCompanyMembershipInput;

const blankMembership = (): MembershipRow => ({
  type: "",
  detail: "",
  status: "Active",
  trade: "",
});

const blankEmployee = (): EmployeeRow => ({
  lastName: "",
  firstName: "",
  email: "",
  phone: "",
  title: "",
  contactType: "",
});

function PicklistSelect({
  id,
  label,
  value,
  options,
  onChange,
  preserveOrder = false,
}: {
  id: string;
  label: string;
  value: string;
  options: PicklistEntry[] | undefined;
  onChange: (next: string) => void;
  preserveOrder?: boolean;
}) {
  const active = activePicklistOptions(options, { preserveOrder });
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <select
        className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
        id={id}
        onChange={(e) => onChange(e.target.value)}
        value={value}
      >
        <option value="">— select —</option>
        {active.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  type?: "text" | "email" | "tel" | "url" | "date";
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>
        {label}
        {required ? " *" : null}
      </Label>
      <Input
        id={id}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        type={type}
        value={value}
      />
    </div>
  );
}

export default function TestCompanyBuilderPage() {
  const [schema, setSchema] = useState<TestCompanySchema | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [accountFields, setAccountFields] = useState<AccountFieldsState>(
    initialAccountFields(),
  );
  const [employees, setEmployees] = useState<EmployeeRow[]>([blankEmployee()]);
  const [memberships, setMemberships] = useState<MembershipRow[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateTestCompanyResponseData | null>(
    null,
  );

  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTestCompanySchema()
      .then((res) => {
        if (!cancelled) setSchema(res.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setSchemaError(
            e instanceof Error
              ? e.message
              : "Failed to load Salesforce schema. Is the BE pointing at a valid SF connection?",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setAccountField = <K extends keyof AccountFieldsState>(
    key: K,
    value: AccountFieldsState[K],
  ) => {
    setAccountFields((prev) => ({ ...prev, [key]: value }));
  };

  const setEmployee = (index: number, patch: Partial<EmployeeRow>) => {
    setEmployees((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  const addEmployee = () => setEmployees((prev) => [...prev, blankEmployee()]);
  const removeEmployee = (index: number) =>
    setEmployees((prev) => prev.filter((_, i) => i !== index));

  const setMembership = (index: number, patch: Partial<MembershipRow>) =>
    setMemberships((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  const addMembership = () =>
    setMemberships((prev) => [...prev, blankMembership()]);
  const removeMembership = (index: number) =>
    setMemberships((prev) => prev.filter((_, i) => i !== index));

  const buildPayloadAccountFields = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    Object.entries(accountFields).forEach(([k, v]) => {
      if (v === "" || v === undefined) return;
      out[k] = v;
    });
    return out;
  };

  const onSeed = async () => {
    setSeedError(null);
    setSeeding(true);
    try {
      const response = await generateMockTestCompany();
      const mock = response.data;

      if (typeof mock.name === "string") setName(mock.name);

      if (mock.accountFields && typeof mock.accountFields === "object") {
        setAccountFields((prev) => {
          const next: AccountFieldsState = { ...prev };
          (Object.keys(prev) as Array<keyof AccountFieldsState>).forEach(
            (key) => {
              const incoming = (mock.accountFields ?? {})[key as string];
              if (incoming === undefined || incoming === null) return;
              if (key === "Eagles__c") {
                (next as Record<string, unknown>)[key] = Boolean(incoming);
              } else if (typeof incoming === "string") {
                (next as Record<string, unknown>)[key] = incoming;
              }
            },
          );
          return next;
        });
      }

      if (Array.isArray(mock.employees) && mock.employees.length > 0) {
        setEmployees(
          mock.employees.map((emp) => ({
            lastName: emp.lastName ?? "",
            firstName: emp.firstName ?? "",
            email: emp.email ?? "",
            phone: emp.phone ?? "",
            title: emp.title ?? "",
            contactType: emp.contactType ?? "",
          })),
        );
      }
    } catch (err) {
      setSeedError(
        err instanceof Error
          ? err.message
          : "Failed to generate mock data. Is OPEN_AI_AUTH_TOKEN set?",
      );
    } finally {
      setSeeding(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setResult(null);
    setSubmitting(true);
    try {
      const payload = {
        name,
        intacctId: null,
        accountFields: buildPayloadAccountFields(),
        employees: employees
          .filter((emp) => emp.lastName.trim() !== "")
          .map((emp) => ({
            lastName: emp.lastName.trim(),
            firstName: emp.firstName?.trim() || undefined,
            email: emp.email?.trim() || undefined,
            phone: emp.phone?.trim() || undefined,
            title: emp.title?.trim() || undefined,
            contactType: emp.contactType?.trim() || undefined,
          })),
        memberships: memberships
          .filter((m) => m.type.trim() !== "")
          .map((m) => ({
            type: m.type.trim(),
            detail: m.detail?.trim() || undefined,
            status: m.status?.trim() || "Active",
            trade: m.trade?.trim() || undefined,
          })),
      };
      const response = await createTestCompany(payload);
      setResult(response.data);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to create test company.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const accountPicklists = useMemo(
    () => schema?.accountPicklists ?? {},
    [schema],
  );
  const membershipPicklists = useMemo(
    () => schema?.membershipPicklists ?? {},
    [schema],
  );

  // Membership_Type_Detail__c is a dependent picklist in SF — only offer
  // details valid under the row's selected type (mirrors Jen's SF config).
  const detailOptionsForType = (type: string): PicklistEntry[] =>
    (membershipPicklists["Membership_Type_Detail__c"] ?? []).filter(
      (o) => !o.validFor || (type !== "" && o.validFor.includes(type)),
    );

  const contactPicklists = useMemo(
    () => schema?.contactPicklists ?? {},
    [schema],
  );

  if (schemaError) {
    return (
      <MainPageWrapper title="Test Company Builder">
        <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3">
          <p className="text-sm text-red-700 dark:text-red-300">
            {schemaError}
          </p>
        </div>
      </MainPageWrapper>
    );
  }

  if (!schema) {
    return (
      <MainPageWrapper title="Test Company Builder">
        <LoadingIndicator />
      </MainPageWrapper>
    );
  }

  return (
    <MainPageWrapper
      subtitle="Spin up a sandbox Salesforce account + sync into HubPlus."
      title="Test Company Builder"
    >
      <form className="max-w-4xl mx-auto space-y-10" onSubmit={onSubmit}>
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Filling these out by hand is tedious — click Seed to have AI
            populate the form with a plausible fake contractor + 5 employees.
          </p>
          <Button
            disabled={seeding}
            onClick={onSeed}
            size="sm"
            type="button"
            variant="outline"
          >
            <Sparkles className="h-4 w-4 mr-1" />
            {seeding ? "Seeding…" : "Seed with mock data"}
          </Button>
        </div>

        {seedError ? (
          <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3">
            <p className="text-sm text-red-700 dark:text-red-300">
              {seedError}
            </p>
          </div>
        ) : null}

        {result ? (
          <div
            className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3"
            data-testid="test-company-result"
          >
            <p className="text-sm text-emerald-800 dark:text-emerald-200">
              Created <strong>{result.companyName}</strong> · SF id{" "}
              <code className="font-mono text-xs">{result.salesforceId}</code> ·
              IntacctID{" "}
              <code className="font-mono text-xs">{result.intacctId}</code> ·{" "}
              {result.employeeSalesforceIds.length} contact
              {result.employeeSalesforceIds.length === 1 ? "" : "s"}
            </p>
          </div>
        ) : null}

        {submitError ? (
          <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3">
            <p className="text-sm text-red-700 dark:text-red-300">
              {submitError}
            </p>
          </div>
        ) : null}

        <section className="space-y-4">
          <h2 className="text-base font-normal text-gray-900 dark:text-white">
            Identity
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              id="name"
              label="Company name"
              onChange={setName}
              required
              value={name}
            />
            <PicklistSelect
              id="Account_Status__c"
              label="Account_Status__c"
              onChange={(v) => setAccountField("Account_Status__c", v)}
              options={accountPicklists["Account_Status__c"]}
              value={accountFields.Account_Status__c}
            />
            <PicklistSelect
              id="CertainPath_Member_Status__c"
              label="CertainPath_Member_Status__c (must be set or HubPlus shuts the company down)"
              onChange={(v) =>
                setAccountField("CertainPath_Member_Status__c", v)
              }
              options={accountPicklists["CertainPath_Member_Status__c"]}
              value={accountFields.CertainPath_Member_Status__c}
            />
            <TextField
              id="Primary_Member_Email__c"
              label="Primary_Member_Email__c"
              onChange={(v) => setAccountField("Primary_Member_Email__c", v)}
              type="email"
              value={accountFields.Primary_Member_Email__c}
            />
            <TextField
              id="Intacct_Contact_Email__c"
              label="Intacct_Contact_Email__c"
              onChange={(v) => setAccountField("Intacct_Contact_Email__c", v)}
              type="email"
              value={accountFields.Intacct_Contact_Email__c}
            />
            <TextField
              id="Website"
              label="Website"
              onChange={(v) => setAccountField("Website", v)}
              type="url"
              value={accountFields.Website}
            />
            <TextField
              id="Phone"
              label="Phone"
              onChange={(v) => setAccountField("Phone", v)}
              type="tel"
              value={accountFields.Phone}
            />
            <TextField
              id="Primary_Join_Date__c"
              label="Primary_Join_Date__c"
              onChange={(v) => setAccountField("Primary_Join_Date__c", v)}
              type="date"
              value={accountFields.Primary_Join_Date__c}
            />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-base font-normal text-gray-900 dark:text-white">
            Billing Address
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              id="BillingStreet"
              label="Street"
              onChange={(v) => setAccountField("BillingStreet", v)}
              value={accountFields.BillingStreet}
            />
            <TextField
              id="BillingCity"
              label="City"
              onChange={(v) => setAccountField("BillingCity", v)}
              value={accountFields.BillingCity}
            />
            <TextField
              id="BillingState"
              label="State"
              onChange={(v) => setAccountField("BillingState", v)}
              value={accountFields.BillingState}
            />
            <TextField
              id="BillingPostalCode"
              label="Postal Code"
              onChange={(v) => setAccountField("BillingPostalCode", v)}
              value={accountFields.BillingPostalCode}
            />
            <TextField
              id="BillingCountry"
              label="Country"
              onChange={(v) => setAccountField("BillingCountry", v)}
              value={accountFields.BillingCountry}
            />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-base font-normal text-gray-900 dark:text-white">
            Shipping Address
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              id="ShippingStreet"
              label="Street"
              onChange={(v) => setAccountField("ShippingStreet", v)}
              value={accountFields.ShippingStreet}
            />
            <TextField
              id="ShippingCity"
              label="City"
              onChange={(v) => setAccountField("ShippingCity", v)}
              value={accountFields.ShippingCity}
            />
            <TextField
              id="ShippingState"
              label="State"
              onChange={(v) => setAccountField("ShippingState", v)}
              value={accountFields.ShippingState}
            />
            <TextField
              id="ShippingPostalCode"
              label="Postal Code"
              onChange={(v) => setAccountField("ShippingPostalCode", v)}
              value={accountFields.ShippingPostalCode}
            />
            <TextField
              id="ShippingCountry"
              label="Country"
              onChange={(v) => setAccountField("ShippingCountry", v)}
              value={accountFields.ShippingCountry}
            />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-base font-normal text-gray-900 dark:text-white">
            Trades
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Affinity__c is a formula derived from these — set the *_Status__c
            picklists and SF computes Affinity__c.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PicklistSelect
              id="ASI_Status__c"
              label="ASI_Status__c"
              onChange={(v) => setAccountField("ASI_Status__c", v)}
              options={accountPicklists["ASI_Status__c"]}
              value={accountFields.ASI_Status__c}
            />
            <TextField
              id="ASI_Join_Date__c"
              label="ASI_Join_Date__c"
              onChange={(v) => setAccountField("ASI_Join_Date__c", v)}
              type="date"
              value={accountFields.ASI_Join_Date__c}
            />
            <PicklistSelect
              id="ESIStatus__c"
              label="ESIStatus__c"
              onChange={(v) => setAccountField("ESIStatus__c", v)}
              options={accountPicklists["ESIStatus__c"]}
              value={accountFields.ESIStatus__c}
            />
            <TextField
              id="ESI_Join_Date__c"
              label="ESI_Join_Date__c"
              onChange={(v) => setAccountField("ESI_Join_Date__c", v)}
              type="date"
              value={accountFields.ESI_Join_Date__c}
            />
            <PicklistSelect
              id="PSIStatus__c"
              label="PSIStatus__c"
              onChange={(v) => setAccountField("PSIStatus__c", v)}
              options={accountPicklists["PSIStatus__c"]}
              value={accountFields.PSIStatus__c}
            />
            <TextField
              id="PSI_Join_Date__c"
              label="PSI_Join_Date__c"
              onChange={(v) => setAccountField("PSI_Join_Date__c", v)}
              type="date"
              value={accountFields.PSI_Join_Date__c}
            />
            <PicklistSelect
              id="RSIStatus__c"
              label="RSIStatus__c"
              onChange={(v) => setAccountField("RSIStatus__c", v)}
              options={accountPicklists["RSIStatus__c"]}
              value={accountFields.RSIStatus__c}
            />
            <TextField
              id="RSI_Join_Date__c"
              label="RSI_Join_Date__c"
              onChange={(v) => setAccountField("RSI_Join_Date__c", v)}
              type="date"
              value={accountFields.RSI_Join_Date__c}
            />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-base font-normal text-gray-900 dark:text-white">
            Access
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PicklistSelect
              id="Stochastic_Marketing_Status__c"
              label="Stochastic_Marketing_Status__c"
              onChange={(v) =>
                setAccountField("Stochastic_Marketing_Status__c", v)
              }
              options={accountPicklists["Stochastic_Marketing_Status__c"]}
              value={accountFields.Stochastic_Marketing_Status__c}
            />
            <div className="flex items-center gap-2 mt-7">
              <input
                checked={accountFields.Eagles__c}
                id="Eagles__c"
                onChange={(e) => setAccountField("Eagles__c", e.target.checked)}
                type="checkbox"
              />
              <Label htmlFor="Eagles__c">Eagles__c</Label>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-normal text-gray-900 dark:text-white">
              Employees / Contacts
            </h2>
            <Button
              onClick={addEmployee}
              size="sm"
              type="button"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add employee
            </Button>
          </div>
          {employees.map((emp, idx) => (
            <div
              className="rounded-lg border border-gray-200 dark:border-slate-700 p-4 space-y-3"
              key={idx}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Employee #{idx + 1}
                </p>
                {employees.length > 1 ? (
                  <Button
                    onClick={() => removeEmployee(idx)}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextField
                  id={`emp-${idx}-lastName`}
                  label="Last name"
                  onChange={(v) => setEmployee(idx, { lastName: v })}
                  required
                  value={emp.lastName}
                />
                <TextField
                  id={`emp-${idx}-firstName`}
                  label="First name"
                  onChange={(v) => setEmployee(idx, { firstName: v })}
                  value={emp.firstName ?? ""}
                />
                <TextField
                  id={`emp-${idx}-email`}
                  label="Email"
                  onChange={(v) => setEmployee(idx, { email: v })}
                  type="email"
                  value={emp.email ?? ""}
                />
                <TextField
                  id={`emp-${idx}-phone`}
                  label="Phone"
                  onChange={(v) => setEmployee(idx, { phone: v })}
                  type="tel"
                  value={emp.phone ?? ""}
                />
                <TextField
                  id={`emp-${idx}-title`}
                  label="Title"
                  onChange={(v) => setEmployee(idx, { title: v })}
                  value={emp.title ?? ""}
                />
                <PicklistSelect
                  id={`emp-${idx}-contactType`}
                  label="Contact_Type__c"
                  onChange={(v) => setEmployee(idx, { contactType: v })}
                  options={contactPicklists["Contact_Type__c"]}
                  value={emp.contactType ?? ""}
                />
              </div>
            </div>
          ))}
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-normal text-gray-900 dark:text-white">
              Memberships
            </h2>
            <Button
              onClick={addMembership}
              size="sm"
              type="button"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add membership
            </Button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Creates real Membership records on the Salesforce Account, mirrored
            into Hub by the sync. &quot;Stage Growth&quot; + a detail of
            Build/Grow/Lead/Scale makes a staged company;
            &quot;CertainPath&nbsp;Membership&quot; grants Total Access; a
            company whose only records are Cancelled gets booted at login. Leave
            empty for a company with no membership rows (sees everything).
          </p>
          {memberships.map((m, idx) => (
            <div
              className="rounded-lg border border-gray-200 dark:border-slate-700 p-4 space-y-3"
              key={idx}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Membership #{idx + 1}
                </p>
                <Button
                  onClick={() => removeMembership(idx)}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <PicklistSelect
                  id={`membership-${idx}-type`}
                  label="Membership_Type__c"
                  onChange={(v) =>
                    setMembership(idx, {
                      type: v,
                      // keep the detail only if it's still valid under the
                      // newly selected type
                      detail: detailOptionsForType(v).some(
                        (o) => o.value === m.detail,
                      )
                        ? m.detail
                        : "",
                    })
                  }
                  options={membershipPicklists["Membership_Type__c"]}
                  value={m.type}
                />
                <PicklistSelect
                  id={`membership-${idx}-detail`}
                  label="Membership_Type_Detail__c"
                  onChange={(v) => setMembership(idx, { detail: v })}
                  options={detailOptionsForType(m.type)}
                  // Stage Growth details are the stage ladder — keep it in order.
                  preserveOrder={m.type === "Stage Growth"}
                  value={m.detail ?? ""}
                />
                <PicklistSelect
                  id={`membership-${idx}-status`}
                  label="Status__c"
                  onChange={(v) => setMembership(idx, { status: v })}
                  options={membershipPicklists["Status__c"]}
                  value={m.status ?? ""}
                />
                <PicklistSelect
                  id={`membership-${idx}-trade`}
                  label="Trade__c"
                  onChange={(v) => setMembership(idx, { trade: v })}
                  options={membershipPicklists["Trade__c"]}
                  value={m.trade ?? ""}
                />
              </div>
            </div>
          ))}
        </section>

        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-slate-700">
          <Button disabled={submitting || name.trim() === ""} type="submit">
            {submitting ? "Creating…" : "Create test company"}
          </Button>
        </div>
      </form>
    </MainPageWrapper>
  );
}
